import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useML } from '../../contexts/MLContext';
import { getAuthHeaders, WORKER_URL } from '../../utils/api';

const BRAZIL_TIMEZONE = 'America/Sao_Paulo';
const API_URL = `${WORKER_URL}/api/sales`;

const formatCurrency = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL'
}).format;

// Componente do Tooltip personalizado
const CustomTooltip = ({ active, payload, label, showValues }) => {
  if (!active || !payload?.length) return null;

  return (
    <div className="bg-white p-4 border border-gray-200 rounded-lg shadow-sm">
      <p className="text-sm font-medium mb-2">Hora: {label}</p>
      {payload.map(({ name, value, color }) => (
        <p key={name} className="text-sm" style={{ color }}>
          <span className="font-medium">{name}:</span>{' '}
          {showValues ? formatCurrency(value) : 'R$ ****'}
        </p>
      ))}
    </div>
  );
};

const SalesChart = ({ menuOpen, data, showValues }) => {
  const [salesData, setSalesData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const wsRef = useRef(null);
  const { setWsStatus } = useML();

  const generateEmptyData = useCallback(() => 
    Array.from({ length: 24 }, (_, hour) => ({
      hora: `${hour.toString().padStart(2, '0')}:00`,
      hoje: 0,
      ontem: 0,
      key: `hour-${hour}`
    })), []);

    const processOrdersData = useCallback((orders) => {
      if (!orders?.length) return generateEmptyData();
  
      const hourlyData = generateEmptyData();
      const now = new Date();
      const currentHour = now.getHours(); // Pega a hora atual
      
      const today = new Date(now.toLocaleString('en-US', { timeZone: BRAZIL_TIMEZONE }));
      today.setHours(0, 0, 0, 0);
      
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
  
      orders.forEach(order => {
          try {
              const orderDate = new Date(order.date_created);
              const brazilDate = new Date(orderDate.toLocaleString('en-US', { timeZone: BRAZIL_TIMEZONE }));
              const orderHour = brazilDate.getHours();
              const amount = parseFloat(order.total_amount) || 0;
              
              const orderDay = new Date(brazilDate);
              orderDay.setHours(0, 0, 0, 0);
  
              if (orderDay.getTime() === today.getTime()) {
                  // Só adiciona vendas de hoje se a hora do pedido for menor ou igual à hora atual
                  if (orderHour <= currentHour) {
                      hourlyData[orderHour].hoje += amount;
                  }
              } else if (orderDay.getTime() === yesterday.getTime()) {
                  hourlyData[orderHour].ontem += amount;
              }
          } catch (error) {
              console.error('Error processing order:', error);
          }
      });
  
      // Zera os valores de "hoje" para horas futuras
      for (let hour = currentHour + 1; hour < 24; hour++) {
          hourlyData[hour].hoje = null; // Usando null em vez de 0 para que a linha não seja plotada
      }
  
      return hourlyData;
  }, [generateEmptyData]);

  const fetchSales = useCallback(async () => {
    try {
      const response = await fetch(API_URL, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const processedData = processOrdersData(data?.results);
      setSalesData(processedData);
      setError(null);
    } catch (error) {
      console.error('Failed to fetch sales:', error);
      setError(error.message);
      setSalesData(generateEmptyData());
    } finally {
      setIsLoading(false);
    }
  }, [generateEmptyData, processOrdersData]);

  useEffect(() => {
    fetchSales();
  }, [fetchSales]);

  const renderChart = useMemo(() => (
    <ResponsiveContainer width="100%" height={250}>
      <LineChart
        data={salesData}
        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5" />
        <XAxis
          dataKey="hora"
          height={30}
          tick={{ fontSize: 11 }}
        />
        <YAxis
          width={60}
          tick={{ fontSize: 11 }}
          tickFormatter={(value) => showValues ? formatCurrency(value) : 'R$ ****'}
        />
        <Tooltip content={(props) => <CustomTooltip {...props} showValues={showValues} />} />
        <Legend wrapperStyle={{ paddingTop: '0px' }} />
        <Line
          type="monotone"
          dataKey="hoje"
          stroke="#2D3277"
          name="Vendas Hoje"
          strokeWidth={2}
          dot={false}
          isAnimationActive={true}
          animationDuration={1000}
        />
        <Line
          type="monotone"
          dataKey="ontem"
          stroke="#8884d8"
          name="Vendas Ontem"
          strokeWidth={2}
          dot={false}
          strokeOpacity={0.5}
          isAnimationActive={false}
        />
      </LineChart>
    </ResponsiveContainer>
  ), [salesData, showValues]);

  return (
    <div className="bg-white p-4 rounded-lg shadow-sm" style={{ height: '100%' }}>
        {isLoading ? (
            <div className="flex justify-center items-center h-[300px]">
                <span className="text-gray-600">Carregando dados...</span>
            </div>
        ) : error ? (
            <div className="flex justify-center items-center h-[300px]">
                <span className="text-red-500">Erro ao carregar dados: {error}</span>
            </div>
        ) : (
            <div style={{ width: '100%', height: 'calc(100% - 10px)' }}>
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                        data={salesData}
                        margin={{ top: 24, right: 30, left: 20, bottom: 0 }}
                    >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5" />
                        <XAxis
                            dataKey="hora"
                            height={20}
                            tick={{ fontSize: 11 }}
                        />
                        <YAxis
                            width={60}
                            tick={{ fontSize: 11 }}
                            tickFormatter={(value) => showValues ? formatCurrency(value) : 'R$ ****'}
                        />
                        <Tooltip content={(props) => <CustomTooltip {...props} showValues={showValues} />} />
                        <Legend
                            verticalAlign="top"
                            align="right"
                            iconSize={10}
                            wrapperStyle={{ fontSize: '11px', paddingRight: '10px', top: 2 }}
                        />
                        <Line
                            type="monotone"
                            dataKey="hoje"
                            stroke="#2D3277"
                            name="Vendas Hoje"
                            strokeWidth={2}
                            dot={false}
                            isAnimationActive={true}
                            animationDuration={1000}
                        />
                        <Line
                            type="monotone"
                            dataKey="ontem"
                            stroke="#8884d8"
                            name="Vendas Ontem"
                            strokeWidth={2}
                            dot={false}
                            strokeOpacity={0.5}
                            isAnimationActive={false}
                        />
                    </LineChart>
                </ResponsiveContainer>
            </div>
        )}
    </div>
);
};

export default SalesChart;